import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

/**
 * Tipi fiduciario in scope per la gestione dei Codici SOL.
 * Esclusi in questo rilascio: BODYSHOP, HAIL_PROVIDER, NETWORK.
 */
export type SolProfessionalType = 'AUTO_EXPERT' | 'RE_EXPERT' | 'INVESTIGATOR' | 'DOCTOR' | 'LAWYER';

/**
 * Dominio chiuso del "Gerarchia – Suffisso" per tipologia fiduciario.
 * Le tipologie non presenti (es. LAWYER) non mostrano il campo.
 */
export const SOL_HIERARCHY_SUFFIXES: Partial<Record<SolProfessionalType, readonly string[]>> = {
  AUTO_EXPERT: ['perito', 'sub', 'conv', 'glass', 'provider grandine', 'authority', 'ne', 'NETWORK', 'fast payment', 'natante'],
  RE_EXPERT: ['perito', 'sub'],
  INVESTIGATOR: ['inv', 'sub'],
  DOCTOR: ['med', 'sub'],
};

/**
 * Sede operativa del fiduciario. Oggi singola; il modello è già a collezione
 * per accogliere la futura gestione multi-sede senza impatti.
 * Se il fiduciario non ha sede operativa, la sede coincide con la sede legale.
 */
export interface OperationalSite {
  id: number;
  /** Indirizzo della sede con i codici grezzi (non decodificati); usare i campi sotto per il display. */
  address: string;
  // Campi indirizzo grezzi: la decodifica (tipo via) avviene lato FE.
  /** Codice del tipo via/toponimo (dominio PLACE_NAME), da decodificare. */
  streetType: string | null;
  streetName: string | null;
  streetNumber: string | null;
  zipCode: string | null;
  /** Codice comune (catastale/nazionale), da decodificare in nome lato FE. */
  municipality: string | null;
  /** Sigla provincia (già leggibile). */
  province: string | null;
  /** True quando la sede coincide con la sede legale (nessuna sede operativa dedicata). */
  legalFallback: boolean;
}

/** Stato del ciclo di vita del codice SOL (valori allineati all'enum BE `SolCodeStatus`). */
export type SolCodeStatus = 'TO_ACTIVATE' | 'ACTIVE' | 'SUSPENDED' | 'INACTIVE';

/**
 * Codice SOL del fiduciario. Il Back Office censisce il codice (master unico per
 * fiduciario + eventuali SOL aggiuntivi); la mail principale e il responsabile
 * sono raccolti dal fiduciario nella fase MFA.
 */
export interface SolCode {
  /** Identificativo progressivo cronologico, generato dal sistema. */
  id: number;
  professionalId: string;
  /** Sede (link figurativo) a cui il codice SOL è associato. */
  siteId: number;
  /** Codice SOL: 5 caratteri alfanumerici, univoco a livello globale. */
  code: string;
  /** Denominazione del codice SOL (precompilata dai dati anagrafici, editabile dal BO). */
  denomination: string | null;
  /** Categoria: valorizzata automaticamente dal tipo fiduciario, editabile. */
  category: string;
  /** Note descrittive (facoltative). */
  description: string;
  /** Stato del ciclo di vita del codice SOL. */
  status: SolCodeStatus;
  /** Mail principale del SOL, inserita in fase MFA; null finché non compilata. */
  principalEmail: string | null;
  /** Responsabile della mail principale (fase MFA). */
  principalHolderFirstName: string | null;
  principalHolderLastName: string | null;
  principalHolderFiscalCode: string | null;
  /** True se il SOL è su sede fisica; false se su territorio (regione/provincia valorizzate). */
  physicalSite: boolean;
  /** Regione operativa (valorizzata per i SOL su territorio). */
  region: string | null;
  /** Provincia operativa (valorizzata per i SOL su territorio). */
  province: string | null;
  /** "Gerarchia – Codice SOL": testo libero max 5, precompilato ed editabile; vuoto per il master. */
  hierarchyParentCode: string | null;
  /** "Gerarchia – Suffisso": valore del dominio chiuso per tipologia; null se non applicabile. */
  hierarchySuffix: string | null;
  /** Indirizzo della sede associata (Riferimento indirizzo sede). */
  siteAddress: string;
  /** True quando il codice è il master del fiduciario (gerarchia padre vuota). */
  master: boolean;
  /** True quando la "Gerarchia – Codice SOL" coincide con il codice stesso. */
  hierarchySameAsCode: boolean;
}

/** Payload di censimento (Back Office) di un codice SOL. La mail è raccolta in fase MFA. */
export interface SolCodeUpsert {
  /** Sede a cui associare il codice; null usa la sede singola derivata dal BE. */
  siteId: number | null;
  code: string;
  /** Denominazione del codice SOL (precompilata FE, editabile BO). */
  denomination: string | null;
  category: string;
  description: string;
  /** True se il SOL è su sede fisica; false se su territorio. */
  physicalSite: boolean;
  /** Regione operativa (valorizzata per i SOL su territorio). */
  region: string | null;
  /** Provincia operativa (valorizzata per i SOL su territorio). */
  province: string | null;
  /** True se il codice è il master del fiduciario (al più uno per fiduciario). */
  master: boolean;
  /** True se la "Gerarchia – Codice SOL" coincide con il codice stesso. */
  hierarchySameAsCode: boolean;
  hierarchyParentCode: string | null;
  hierarchySuffix: string | null;
}

// ── Fase MFA (codici SOL) ─────────────────────────────────────────────────────

/** Persona selezionabile nel wizard MFA (collaboratore o impiegato amministrativo). */
export interface MfaPerson {
  /** Id su collaborators.collaborator_id. */
  collaboratorId: number;
  firstName: string;
  lastName: string;
  fiscalCode: string | null;
  /** Ruolo (valorizzato per gli impiegati amministrativi). */
  role: string | null;
  /** Origine: COLLABORATOR (collaboratore) o EMPLOYEE (impiegato amministrativo). */
  kind: 'COLLABORATOR' | 'EMPLOYEE';
}

/** Stato dell'associazione persona↔SOL: PENDING = bozza editabile; ACTIVE = validata (bloccata). */
export type MfaAssociationStatus = 'PENDING' | 'ACTIVE';

/** Associazione SOL <-> persona nel dato MFA (in lettura). */
export interface MfaAssociation {
  id: number;
  collaboratorId: number;
  firstName: string;
  lastName: string;
  role: string | null;
  kind: 'COLLABORATOR' | 'EMPLOYEE';
  accessEmail: string;
  /** PENDING = bozza modificabile; ACTIVE = validata all'approvazione MFA, in sola lettura. */
  status: MfaAssociationStatus;
}

/** Stato MFA corrente di un codice SOL. */
export interface SolCodeMfa {
  solCodeId: number;
  code: string;
  status: SolCodeStatus;
  master: boolean;
  /** Denominazione assegnata al SOL lato Back Office (aiuta il fiduciario a identificarlo). */
  denomination: string | null;
  /** True se il SOL è su sede fisica; false se su territorio (region/province valorizzati). */
  physicalSite: boolean;
  /** Regione operativa (codice/nome grezzo, valorizzata per i SOL su territorio); da decodificare. */
  region: string | null;
  /** Provincia operativa (codice/nome grezzo, valorizzata per i SOL su territorio); da decodificare. */
  province: string | null;
  /** Sede fisica associata al SOL con i campi indirizzo grezzi (tipo via da decodificare). */
  site: OperationalSite | null;
  principalEmail: string | null;
  principalHolderFirstName: string | null;
  principalHolderLastName: string | null;
  principalHolderFiscalCode: string | null;
  /** True quando il SOL ha mail principale + responsabile valorizzati ("Compilato"). */
  compiled: boolean;
  associations: MfaAssociation[];
}

/** Payload di salvataggio (bozza) dei dati MFA di un singolo codice SOL. */
export interface SolCodeMfaSave {
  principalEmail: string;
  principalHolderFirstName: string | null;
  principalHolderLastName: string | null;
  principalHolderFiscalCode: string | null;
  associations: { collaboratorId: number; accessEmail: string }[];
}

/** Riga di sola consultazione della tabella B (impiegato amministrativo/collaboratore). */
export interface CollaboratorSolView {
  collaboratorId: number;
  /** Codice SOL ereditato dalla sede di riferimento. */
  code: string;
  lastName: string;
  firstName: string;
  fiscalCode: string;
  siteAddress: string;
  /** Email di autenticazione ai sistemi gestionali AXA. */
  email: string;
  /** Numero iscrizione albo, valorizzato solo per le tipologie che lo prevedono. */
  registrationNumber: string | null;
}

interface ApiResponse<T> {
  status: boolean;
  message: string;
  data: T | null;
}

/** Stato della sezione "Codici SOL": DRAFT = salva bozza, FILLED = salvata (avanzabile). */
export type SolSectionStatus = 'DRAFT' | 'FILLED' | null;

@Injectable({ providedIn: 'root' })
export class SolCodeService {
  private http = inject(HttpClient);
  private readonly base = `${environment.professionalsApiUrl}/api/v1/professionals`;

  getSites(professionalId: string): Observable<ApiResponse<OperationalSite[]>> {
    return this.http.get<ApiResponse<OperationalSite[]>>(`${this.base}/${professionalId}/operational-sites`);
  }

  getSolCodes(professionalId: string): Observable<ApiResponse<SolCode[]>> {
    return this.http.get<ApiResponse<SolCode[]>>(`${this.base}/${professionalId}/sol-codes`);
  }

  createSolCode(professionalId: string, payload: SolCodeUpsert): Observable<ApiResponse<SolCode>> {
    return this.http.post<ApiResponse<SolCode>>(`${this.base}/${professionalId}/sol-codes`, payload);
  }

  updateSolCode(professionalId: string, solId: number, payload: SolCodeUpsert): Observable<ApiResponse<SolCode>> {
    return this.http.put<ApiResponse<SolCode>>(`${this.base}/${professionalId}/sol-codes/${solId}`, payload);
  }

  deleteSolCode(professionalId: string, solId: number): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.base}/${professionalId}/sol-codes/${solId}`);
  }

  /**
   * Cambio di stato manuale (Back Office) di un codice SOL. Transizioni ammesse lato BE:
   * ACTIVE↔SUSPENDED e ACTIVE/SUSPENDED→INACTIVE (finale, non riattivabile).
   */
  changeSolCodeStatus(
    professionalId: string,
    solId: number,
    status: SolCodeStatus,
  ): Observable<ApiResponse<SolCode>> {
    return this.http.patch<ApiResponse<SolCode>>(
      `${this.base}/${professionalId}/sol-codes/${solId}/status`,
      { status },
    );
  }

  getCollaboratorSolView(professionalId: string): Observable<ApiResponse<CollaboratorSolView[]>> {
    return this.http.get<ApiResponse<CollaboratorSolView[]>>(`${this.base}/${professionalId}/sol-codes/collaborators`);
  }

  exportCollaboratorSol(professionalId: string): Observable<Blob> {
    return this.http.get(`${this.base}/${professionalId}/sol-codes/collaborators/export`, { responseType: 'blob' });
  }

  getSectionStatus(professionalId: string): Observable<ApiResponse<SolSectionStatus>> {
    return this.http.get<ApiResponse<SolSectionStatus>>(`${this.base}/${professionalId}/sol-codes/section-status`);
  }

  /** status FILLED applica il controllo hard lato BE (almeno un codice SOL per ogni sede). */
  updateSectionStatus(professionalId: string, status: 'DRAFT' | 'FILLED'): Observable<ApiResponse<SolSectionStatus>> {
    return this.http.patch<ApiResponse<SolSectionStatus>>(
      `${this.base}/${professionalId}/sol-codes/section-status`,
      { status },
    );
  }

  // ── Fase MFA (codici SOL) ────────────────────────────────────────────────────

  /** Persone selezionabili nel wizard MFA (collaboratori + impiegati con accesso = Sì). */
  getMfaEligiblePeople(professionalId: string): Observable<ApiResponse<MfaPerson[]>> {
    return this.http.get<ApiResponse<MfaPerson[]>>(
      `${this.base}/${professionalId}/sol-codes/mfa/eligible-people`,
    );
  }

  /** Stato MFA corrente di tutti i codici SOL del fiduciario. */
  getMfaData(professionalId: string): Observable<ApiResponse<SolCodeMfa[]>> {
    return this.http.get<ApiResponse<SolCodeMfa[]>>(`${this.base}/${professionalId}/sol-codes/mfa`);
  }

  /** Salva (bozza) i dati MFA di un singolo codice SOL. */
  saveMfaData(
    professionalId: string,
    solCodeId: number,
    payload: SolCodeMfaSave,
  ): Observable<ApiResponse<SolCodeMfa>> {
    return this.http.put<ApiResponse<SolCodeMfa>>(
      `${this.base}/${professionalId}/sol-codes/${solCodeId}/mfa`,
      payload,
    );
  }

  // ── Flusso MFA (transizioni di stato) ─────────────────────────────────────────

  /** BO: apre la compilazione MFA al fiduciario (PENDING → USER_INPUT, mail M1). */
  requestMfaCompilation(professionalId: string): Observable<RegistrationStepDto> {
    return this.http.post<RegistrationStepDto>(`${this.base}/${professionalId}/mfa/request`, {});
  }

  /** Fiduciario: conferma i dati MFA compilati (USER_INPUT → PENDING, mail M2). */
  confirmMfa(professionalId: string): Observable<RegistrationStepDto> {
    return this.http.post<RegistrationStepDto>(`${this.base}/${professionalId}/mfa/confirm`, {});
  }

  /** BO: revisiona i dati MFA (APPROVE → CONTRACT_SENT, REQUEST_MODIFICATION → USER_INPUT). */
  reviewMfa(
    professionalId: string,
    reviewType: 'APPROVE' | 'REQUEST_MODIFICATION',
    notes?: string,
  ): Observable<RegistrationStepDto> {
    return this.http.post<RegistrationStepDto>(`${this.base}/${professionalId}/mfa/review`, {
      reviewType,
      notes: notes ?? null,
    });
  }

  /** BO: salta la fase MFA per i primi impianti con SOL già attivi (→ CONTRACT_SENT). */
  skipMfa(professionalId: string): Observable<RegistrationStepDto> {
    return this.http.post<RegistrationStepDto>(`${this.base}/${professionalId}/mfa/skip`, {});
  }
}

/** Step di registrazione restituito dalle transizioni del flusso MFA. */
export interface RegistrationStepDto {
  id: number;
  professionalId: string;
  step: string;
  stepDescription: string;
  status: string;
  comments: string | null;
  isCurrent: boolean;
}
